import api from './api';
import { Product, ProductFormData, PageResponse, normalizeProduct } from '../types/Product';

export interface ProductPaginationParams {
  page?: number;
  size?: number;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

export interface ProductFilterParams extends ProductPaginationParams {
  searchTerm?: string;
  brand?: string;
  color?: string;
  stockStatus?: 'all' | 'inStock' | 'lowStock' | 'outOfStock';
}

export type BulkPriceRuleMode = 'PERCENT' | 'ABSOLUTE';

export interface BulkPriceUpdatePayload {
  productIds: number[];
  purchasePriceUpdate?: { mode: BulkPriceRuleMode; value: number };
  salePriceUpdate?: { mode: BulkPriceRuleMode; value: number };
}

export const productService = {
  getAll: async (params?: ProductPaginationParams): Promise<PageResponse<Product>> => {
    const response = await api.get<PageResponse<Product>>('/products', {
      params: {
        page: params?.page ?? 0,
        size: params?.size ?? 50,
        sortBy: params?.sortBy ?? 'id',
        sortDir: params?.sortDir ?? 'asc',
      },
    });
    const data = response.data;
    const content = Array.isArray(data.content) ? data.content.map(normalizeProduct) : [];
    return { ...data, content };
  },

  search: async (name: string): Promise<Product[]> => {
    const response = await api.get<Product[]>('/products/search', {
      params: { name },
    });
    return (response.data ?? []).map(normalizeProduct);
  },

  filter: async (params?: ProductFilterParams): Promise<PageResponse<Product>> => {
    const filterParams: Record<string, string | number> = {
      page: params?.page ?? 0,
      size: params?.size ?? 50,
      sortBy: params?.sortBy ?? 'id',
      sortDir: params?.sortDir ?? 'asc',
    };

    if (params?.brand && params.brand.trim()) filterParams.brand = params.brand.trim();
    if (params?.color && params.color.trim()) filterParams.color = params.color.trim();
    if (params?.stockStatus && params.stockStatus !== 'all') {
      if (params.stockStatus === 'lowStock') {
        filterParams.stockStatus = 'low';
      } else if (params.stockStatus === 'outOfStock') {
        filterParams.stockStatus = 'out';
      } else if (params.stockStatus === 'inStock') {
        filterParams.stockStatus = 'inStock';
      }
    }
    const response = await api.get<PageResponse<Product>>('/products', {
      params: filterParams,
    });
    const data = response.data;
    const content = Array.isArray(data.content) ? data.content.map(normalizeProduct) : [];
    return { ...data, content };
  },

  getById: async (id: number): Promise<Product> => {
    const response = await api.get<Product>(`/products/${id}`);
    return normalizeProduct(response.data);
  },

  create: async (data: ProductFormData): Promise<Product> => {
    const payload = {
      ...data,
      quantity: Math.max(0, Math.floor(Number(data.quantity) ?? 0)),
      unitPrice: Number(data.unitPrice) ?? 0,
      retailPrice: Number(data.retailPrice) ?? 0,
    };
    const response = await api.post<Product>('/products', payload);
    return normalizeProduct(response.data);
  },

  update: async (id: number, data: ProductFormData): Promise<Product> => {
    const response = await api.patch<Product>(`/products/${id}`, data);
    return normalizeProduct(response.data);
  },

  bulkUpdatePrices: async (
    payload: BulkPriceUpdatePayload
  ): Promise<{ updated: number; productIds: number[] }> => {
    const response = await api.post<{ updated: number; productIds: number[] }>(
      '/products/bulk-prices',
      payload,
      { timeout: 120000 }
    );
    if (response.status < 200 || response.status >= 300) {
      const data = response.data as { message?: string };
      throw new Error(data?.message ?? 'Toplu fiyat güncellenemedi');
    }
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/products/${id}`);
  },
};
